import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import VerticalTimeline from "./VerticalTimelineWithMilestones";
import BarChartRace from "./BarChartRace";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin,
    annotationPlugin
);

function ChartWithStats({ chartTitle, chartDatasets, chartLabels, startDate, duration, milestones, asdf, xxx, kombiniert = false }) {
    const chartRef = useRef(null);
    const [showAnnotations, setShowAnnotations] = useState(false);
    const [legendCombined, setLegendCombined] = useState(xxx);

    const allChartValues = chartDatasets.flatMap(dataset => dataset.data);
    const numericValues = allChartValues.filter(value => typeof value === 'number');
    const maxValue = Math.ceil(Math.max(...numericValues)) + 1;

    const isMobileDevice = () =>
        /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);

    const mobileStyles = () => {
        if (isMobileDevice()) {
            const container = document.getElementById("container");
            const canvas = document.querySelector("canvas");
            if (container) container.style.maxWidth = "100%";
            if (canvas) {
                canvas.style.width = "100%";
                canvas.style.height = "auto";
                canvas.style.padding = "10px";
            }
        }
    };

    const getSymbolHtml = (sex, type) => {
        if (type) return sex ? (sex === 'male' ? '♂' : '♀') : '';
        return (
            <span style={{ display: 'inline-block', margin: 0, width: 16 }}>
                {sex ? (sex === 'male' ? '♂' : '♀') : ''}
            </span>
        );
    };

    const extendLabelsForForecast = (baseLength, daysToAdd) => {
        const extended = [...chartLabels];
        for (let i = 1; i <= daysToAdd; i++) {
            extended.push(`tag-${baseLength + i}`);
        }
        return extended;
    };

    const computeSimpleForecastDatasets = (daysToForecast) => {
        return chartDatasets
            .map((ds) => {
                if (
                    !ds.label.includes('Faramir') &&
                    !ds.label.includes('Frodo Fíli') &&
                    !ds.label.includes('Fëanor') &&
                    !ds.label.includes('Feamára')
                ) {
                    return null;
                }
                if (!Array.isArray(ds.data) || ds.data.length < 4) return null;
                const diffs = [];
                for (let i = ds.data.length - 1; i > 0; i--) {
                    const curr = ds.data[i];
                    const prev = ds.data[i - 1];
                    if (typeof curr === 'number' && typeof prev === 'number') {
                        diffs.push(curr - prev);
                    }
                    if (diffs.length === 7) break;
                }
                if (!diffs.length) return null;
                const avgDiff = diffs.reduce((acc, val) => acc + val, 0) / diffs.length;
                const forecastedData = new Array(ds.data.length).fill(null);
                forecastedData[ds.data.length - 1] = ds.data[ds.data.length - 1];
                let currentForecastValue = ds.data[ds.data.length - 1];
                for (let i = 1; i <= daysToForecast; i++) {
                    currentForecastValue += avgDiff;
                    forecastedData.push(Number(currentForecastValue.toFixed(2)));
                }
                return {
                    ...ds,
                    label: ds.label + ' Prognose',
                    data: forecastedData,
                    borderDash: [5, 5],
                    // borderColor: 'rgba(0,0,0,0.5)',
                    borderWidth: 5,
                    backgroundColor: ds.borderColor,
                    pointRadius: 0,
                    fill: false,
                    spanGaps: true
                };
            })
            .filter(ds => ds !== null);
    };

    console.log(chartDatasets);
    const daysToForecast = 66 - chartDatasets[0].data.length;
    const forecastedDatasets = computeSimpleForecastDatasets(daysToForecast);
    // const extendedLabels = extendLabelsForForecast(chartLabels.length, daysToForecast);

    const data = {
        labels: chartLabels,
        datasets: [...chartDatasets, ...forecastedDatasets]
    };

    const parseGermanDate = (dateStr) => {
        const parts = dateStr.split('.');
        if (parts.length !== 3) return new Date(dateStr);
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        let year = parseInt(parts[2], 10);
        if (year < 100) year += 2000;
        return new Date(year, month, day);
    };

    const annotationLines = milestones
        ? milestones
            .map((milestone, index) => {
                const birthDate = new Date(startDate[0], startDate[1], startDate[2]);
                const milestoneDate = milestone.date instanceof Date ? milestone.date : parseGermanDate(milestone.date);
                const rawDaysSinceBirth = Math.floor((milestoneDate - birthDate) / (1000 * 60 * 60 * 24));
                if (isNaN(rawDaysSinceBirth) || rawDaysSinceBirth < 0) {
                    console.warn(`Fehler: Ungültige daysSinceBirth für Milestone "${milestone.label}"`);
                    return [];
                }
                const totalDays = chartLabels.length - 1;
                const marginVal = 7;
                let adjustedXValue = rawDaysSinceBirth;
                let textAlign = 'left';
                if (rawDaysSinceBirth > 0 && rawDaysSinceBirth < marginVal) {
                    adjustedXValue = marginVal;
                    textAlign = 'left';
                } else if (rawDaysSinceBirth >= totalDays - marginVal && rawDaysSinceBirth < totalDays) {
                    adjustedXValue = rawDaysSinceBirth;
                    textAlign = 'right';
                }
                let maxYValue = 0;
                chartDatasets.forEach((dataset) => {
                    if (Array.isArray(dataset.data) && dataset.data[rawDaysSinceBirth] !== undefined) {
                        const value = dataset.data[rawDaysSinceBirth];
                        if (value > maxYValue) maxYValue = value;
                    }
                });
                if (index === 1) maxYValue += 0;
                else if (index === 2) maxYValue += 0.27;
                else if (index === 3) maxYValue += 0.51;
                else if (index === 4) maxYValue += 0.74;
                const adjustedYValue = maxYValue;
                const lines = [milestone.formattedDate, ...milestone.label];
                const fontArray = lines.map((_, i) =>
                    i === 0 ? { size: 15, family: 'Aniron' } : { size: 9, family: 'Aniron' }
                );
                const colorArray = lines.map(() => '#fff');
                const labelAnnotation = {
                    type: 'label',
                    xValue: adjustedXValue,
                    yValue: adjustedYValue,
                    backgroundColor: 'rgba(255, 99, 132, 0.9)',
                    content: lines,
                    color: colorArray,
                    font: fontArray,
                    textAlign: textAlign,
                    borderWidth: 1,
                    borderColor: 'rgba(255, 99, 132, 1)',
                    cornerRadius: 4,
                    padding: 6,
                    position: { x: textAlign === 'left' ? 'start' : 'end', y: 'center' },
                };
                const pointAnnotation = {
                    type: 'point',
                    xValue: adjustedXValue,
                    yValue: adjustedYValue,
                    backgroundColor: 'rgba(100, 149, 237, 0.9)',
                    radius: 4,
                    borderColor: '#fff',
                    borderWidth: 0,
                };
                return [labelAnnotation, pointAnnotation];
            })
            .flat()
        : [];

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        layout: {
            padding: {
                top: isMobileDevice() ? 5 : 10,
                left: isMobileDevice() ? 5 : 10,
                right: isMobileDevice() ? 5 : 10,
                bottom: 30,
            },
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    generateLabels: (chart) => {
                        if (legendCombined) {
                            const groups = {};
                            chart.data.datasets.forEach((dataset, index) => {
                                if (dataset.dataType) {
                                    if (!groups[dataset.dataType]) {
                                        groups[dataset.dataType] = { indices: [] };
                                    }
                                    groups[dataset.dataType].indices.push(index);
                                }
                            });
                            const mapping = { F: 'F-Wurf', C: 'C-Wurf', G: 'G-Wurf', Ridgeback: 'Ridgeback' };
                            return Object.keys(groups).map((groupKey) => {
                                const visible = groups[groupKey].indices.every(i => chart.isDatasetVisible(i));
                                return {
                                    text: mapping[groupKey] ? mapping[groupKey] : groupKey,
                                    fillStyle: chart.data.datasets[groups[groupKey].indices[0]].borderColor,
                                    hidden: !visible,
                                    datasetIndices: groups[groupKey].indices
                                };
                            });
                        }
                        return chart.data.datasets.map((dataset, index) => {
                            const sexSymbol = getSymbolHtml(dataset.sex, true);
                            return {
                                text: `${sexSymbol} ${dataset.label}`,
                                fillStyle: dataset.borderColor,
                                strokeStyle: dataset.borderColor,
                                lineWidth: 2,
                                hidden: !chart.isDatasetVisible(index),
                                datasetIndex: index,
                            };
                        });
                    },
                    font: { size: 12, family: 'Aniron' },
                    padding: 20,
                    boxWidth: 20,
                    boxHeight: 10,
                },
                onClick: (e, legendItem, legend) => {
                    const chart = legend.chart;
                    if (legendCombined && legendItem.datasetIndices) {
                        const allVisible = legendItem.datasetIndices.every(i => chart.isDatasetVisible(i));
                        legendItem.datasetIndices.forEach(i => {
                            chart.setDatasetVisibility(i, !allVisible);
                        });
                        chart.update();
                    } else {
                        const index = legendItem.datasetIndex;
                        chart.setDatasetVisibility(index, !chart.isDatasetVisible(index));
                        chart.update();
                    }
                }
            },
            zoom: { pan: { enabled: true, mode: 'x' }, zoom: { drag: { enabled: !isMobileDevice() } } },
            annotation: { annotations: !isMobileDevice() && showAnnotations ? annotationLines : null },
            tooltip: {
                enabled: false,
                external: (context) => {
                    const tooltipEl = document.getElementById('chartjs-tooltip') || document.createElement('div');
                    if (!tooltipEl.id) {
                        tooltipEl.id = 'chartjs-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                        tooltipEl.style.color = '#fff';
                        tooltipEl.style.borderRadius = '5px';
                        tooltipEl.style.padding = '10px';
                        tooltipEl.style.fontSize = '10px';
                        tooltipEl.style.fontFamily = 'Aniron';
                        document.body.appendChild(tooltipEl);
                    }
                    const tooltipModel = context.tooltip;
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = 0;
                        return;
                    }
                    const chart = context.chart;
                    const dataIndex = tooltipModel.dataPoints[0]?.dataIndex;
                    if (dataIndex == null) return;
                    const initialDate = new Date(startDate[0], startDate[1], startDate[2]);
                    const currentDateObj = new Date(initialDate);
                    currentDateObj.setDate(initialDate.getDate() + dataIndex);
                    let currentDate = currentDateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                    const dayDifference = dataIndex;
                    function getBaseLabel(label) {
                        if (!label) return '';
                        const prognosisSuffix = ' Prognose';
                        if (label.endsWith(prognosisSuffix)) {
                            return label.slice(0, label.length - prognosisSuffix.length);
                        }
                        return label;
                    }

                    const dataPointsAll = chart.data.datasets
                        .map((dataset, datasetIndex) => {
                            if (!chart.isDatasetVisible(datasetIndex)) return null;
                            const value = dataset.data[dataIndex];
                            const previousValue = dataIndex > 0 ? dataset.data[dataIndex - 1] : null;
                            const difference = previousValue !== null ? value - previousValue : null;
                            const percentIncrease = previousValue !== null ? ((difference / previousValue) * 100).toFixed(1) : null;
                            return {
                                label: dataset.label,
                                value: value,
                                color: dataset.borderColor,
                                difference: difference,
                                percentIncrease: percentIncrease,
                                isSoll: dataset.label.includes('Soll'),
                                sex: dataset.sex,
                            };
                        })
                        .filter((item) => item !== null && item.value !== undefined);

                    const labelCounts = dataPointsAll.reduce((acc, item) => {
                        const base = getBaseLabel(item.label);
                        acc[base] = (acc[base] || 0) + 1;
                        return acc;
                    }, {});

                    const dataPoints = dataPointsAll.filter(item => {
                        const base = getBaseLabel(item.label);
                        const isPrognosis = item.label.endsWith(' Prognose');
                        if (isPrognosis && labelCounts[base] > 1) {
                            return false;
                        }
                        return true;
                    });




                    // !ds.label.includes('Faramir') &&
                    // !ds.label.includes('Frodo Fíli') &&
                    // !ds.label.includes('Fëanor') &&
                    // !ds.label.includes('Feamára')













                    const sollNiedrigValue = chart.data.datasets.find(d => d.label === 'Soll (-)')?.data[dataIndex];
                    const sollHochValue = chart.data.datasets.find(d => d.label === 'Soll (+)')?.data[dataIndex];
                    const sortedDataPoints = [...dataPoints].sort((a, b) => b.value - a.value);
                    const rankedDataPoints = [...dataPoints]
                        .filter((item) => !item.isSoll && item.difference !== null)
                        .sort((a, b) => b.difference - a.difference + (b.value - a.value ? -0.0001 : 0.0001))
                        .map((item, index) => ({ ...item, rank: index + 1 }));
                    const dataPointsWithRank = sortedDataPoints.map((item) => {
                        const rankEntry = rankedDataPoints.find(rankedItem => rankedItem.label === item.label);
                        let weightColor = '#f00';
                        if (item.value >= sollNiedrigValue && item.value <= sollHochValue) weightColor = '#0f0';
                        else if (item.value > sollHochValue) weightColor = '#ffa500';
                        return { ...item, rank: rankEntry ? rankEntry.rank : null, weightColor };
                    });
                    let innerHtml = `<div style="font-weight: bold; margin-bottom: 5px; text-align: center;">${currentDate} (Tag ${dayDifference+1})</div>`;
                    innerHtml += `<table style="width: 100%; border-spacing: 0; border-collapse: collapse;">`;
                    dataPointsWithRank.forEach((item) => {
                        if (!item.value) return null;
                        const formattedValue = `${(item.value >= 10 ? item.value.toFixed(1) : item.value.toFixed(2)).replace('.', ',')} kg`;
                        const formattedDifference = item.difference !== null ? `${item.difference > 0 ? '+' : ''}${Math.round(item.difference * 1000)} g` : '';
                        const formattedPercentIncrease = item.percentIncrease !== null ? `${item.percentIncrease > 0 ? '+' : ''}${item.percentIncrease} %` : '';
                        const rank = item.rank !== null ? `(${item.rank})` : '-';
                        innerHtml += `
              <tr style="font-family: Aniron;">
                <td style="text-align: left; padding: 2px; font-size: 10px;">
                  <div style="display: inline-block; width: 10px; height: 10px; background-color: ${item.color}; border-radius: 50%; margin-right: 5px;"></div>
                  <span style="display: inline-block; margin: 0; width: 10px;">
                    ${item.sex ? (item.sex === 'male' ? '♂' : '♀') : ''}
                  </span> ${item.label}:
                </td>
                <td style="text-align: right; padding: 2px; color: ${item.weightColor};">
                  ${formattedValue}
                </td>
                <td style="text-align: right; padding: 2px; color: ${item.difference > 0 ? '#0f0' : '#f00'};">
                  ${formattedDifference}
                </td>
                <td style="text-align: right; padding: 2px;">
                  ${formattedPercentIncrease}
                </td>
                <td style="text-align: center; padding: 2px;">${rank}</td>
              </tr>
            `;
                    });
                    innerHtml += `</table>`;
                    tooltipEl.innerHTML = innerHtml;
                    const chartBounds = chart.canvas.getBoundingClientRect();
                    const tooltipWidth = tooltipEl.offsetWidth;
                    const tooltipHeight = tooltipEl.offsetHeight;
                    const caretX = tooltipModel.caretX;
                    const caretY = tooltipModel.caretY;
                    let tooltipLeft = chartBounds.left + caretX;
                    let tooltipTop = chartBounds.top + caretY + 10;
                    if (tooltipLeft + tooltipWidth / 2 > window.innerWidth) tooltipLeft = window.innerWidth - tooltipWidth / 2 - 10;
                    if (tooltipLeft - tooltipWidth / 2 < 0) tooltipLeft = 10 + tooltipWidth / 2;
                    if (tooltipTop + tooltipHeight > window.innerHeight) tooltipTop = window.innerHeight - tooltipHeight - 10;
                    tooltipEl.style.left = `${tooltipLeft}px`;
                    tooltipEl.style.top = `${tooltipTop}px`;
                    tooltipEl.style.opacity = 1;
                },
            },
        },
        scales: {
            x: {
                type: 'category',
                ticks: {
                    callback: function (value, index, values) {
                        const maxLabels = isMobileDevice() ? 10 : 20;
                        const stepSize = Math.max(1, Math.ceil(values.length / maxLabels));
                        if (index % stepSize === 0) {
                            if (kombiniert) {
                                return `${index+1}`;
                            } else {
                                const initialDate = new Date(startDate[0], startDate[1], startDate[2]);
                                const currentDate = new Date(initialDate);
                                currentDate.setDate(initialDate.getDate() + index);
                                return currentDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                            }
                        }
                        return '';
                    },
                    autoSkip: false,
                    maxRotation: 35,
                    minRotation: 0,
                    font: { family: 'Aniron' },
                },
            },
            y: {
                beginAtZero: true,
                ticks: { font: { family: 'Aniron' }, padding: 10 },
                max: maxValue,
            },
        },
    };

    if (kombiniert) {
        options.scales.x.title = {
            display: true,
            text: 'Alter in Tagen',
            font: {
                family: 'Aniron',
                size: 14,
            },
            padding: {
                top: 15
            }
        };
    }

    const resetZoom = () => {
        if (chartRef.current) chartRef.current.resetZoom();
    };

    const currentDateIndex = chartDatasets[0].data.length - 1;
    const initialDate = new Date(startDate[0], startDate[1], startDate[2]);
    const currentDateObj = new Date(initialDate);
    currentDateObj.setDate(initialDate.getDate() + currentDateIndex);
    const currentDate = currentDateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

    const generateSortedWeightValues = () => {
        const nonSollDatasets = chartDatasets.filter(ds => !ds.label.includes('Soll'));
        const sollNiedrigValue = chartDatasets.find(d => d.label === 'Soll (-)')?.data[currentDateIndex];
        const sollHochValue = chartDatasets.find(d => d.label === 'Soll (+)')?.data[currentDateIndex];
        const weightValues = nonSollDatasets.map(ds => {
            const value = ds.data[currentDateIndex];
            const dayOne = ds.data[0];
            let color = '#f00';
            if (sollNiedrigValue !== undefined && sollHochValue !== undefined) {
                if (value >= sollNiedrigValue && value <= sollHochValue) color = '#0f0';
                else if (value > sollHochValue) color = '#ffa500';
            }
            return { type: ds.label, value, dayOne, color: ds.borderColor, sex: ds.sex, displayColor: color };
        });
        weightValues.push(
            { type: 'Soll (-)', value: sollNiedrigValue, dayOne: null, color: 'rgba(0, 0, 0, 1)', displayColor: '#fff' },
            { type: 'Soll (+)', value: sollHochValue, dayOne: null, color: 'rgba(169, 169, 169, 1)', displayColor: '#fff' }
        );
        return weightValues.sort((a, b) => b.value - a.value);
    };

    const sortedWeightValues = generateSortedWeightValues();

    const generateSortedPercentValues = () => {
        const nonSollDatasets = chartDatasets.filter(ds => !ds.label.includes('Soll'));
        return nonSollDatasets
            .map(ds => {
                const value = ds.data[currentDateIndex];
                const dayOne = ds.data[0];
                const percentIncrease = dayOne !== 0 ? ((value * 100) / dayOne - 100).toFixed(1) : '0.0';
                const difference = value - dayOne;
                return { type: ds.label, value, dayOne, color: ds.borderColor, sex: ds.sex, percentIncrease, difference };
            })
            .sort((a, b) => (b.value * 100) / b.dayOne - 100 - ((a.value * 100) / a.dayOne - 100));
    };

    const sortedPercentValues = generateSortedPercentValues();

    useEffect(() => {
        mobileStyles();
        window.addEventListener("resize", mobileStyles);
        setTimeout(() => { chartRef.current.update(); }, 0);
        return () => { window.removeEventListener("resize", mobileStyles); };
    }, []);

    return (
        <div id="container" style={{ fontFamily: 'Aniron', margin: 'auto' }}>
            <div style={{ textAlign: 'center' }}>
                <h1 style={{ marginTop: 0, paddingTop: 10, fontFamily: 'Aniron', fontSize: 22 }}>{chartTitle}</h1>
                {!isMobileDevice() && (
                    <button onClick={resetZoom} className="chart-button" style={{ fontFamily: 'Aniron' }}>
                        Zoom zurücksetzen
                    </button>
                )}
                {kombiniert && (
                    <button
                        onClick={() => setLegendCombined(prev => !prev)}
                        className="chart-button"
                        style={{fontFamily: 'Aniron'}}
                    >
                        {legendCombined ? 'Individuelle Legende anzeigen' : 'Gruppierte Legende anzeigen'}
                    </button>
                )}
                {(!isMobileDevice() && annotationLines.length > 0 && false) && (
                    <button onClick={() => setShowAnnotations(!showAnnotations)} className="chart-button"
                            style={{fontFamily: 'Aniron'}}>
                        {showAnnotations ? 'Anmerkungen ausblenden' : 'Anmerkungen einblenden'}
                    </button>
                )}
            </div>
            <div id="chartjs-tooltip" className="chartjs-tooltip"></div>
            <div className="chart-container">
                <Line ref={chartRef} data={data} options={options} height={400} style={{ maxHeight: 575 }} />
            </div>
            {!kombiniert ? (
                <div style={{
                    paddingBottom: 10,
                    display: 'flex',
                    flexWrap: isMobileDevice() ? 'wrap' : 'inherit',
                    justifyContent: 'center',
                    maxWidth: 800,
                    margin: 'auto',
                    flexDirection: 'row',
                    gap: 10
                }}>
                    <div style={{
                        width: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: '#fff',
                        borderRadius: '5px',
                        padding: '10px 5px',
                        fontSize: '12px',
                        fontFamily: 'Aniron',
                        margin: isMobileDevice() ? '0 10px' : 0
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px', textAlign: 'center' }}>
                            Gewicht in kg<br />{currentDate} (Tag {currentDateIndex + 1})
                        </div>
                        <table style={{ width: '100%' }}>
                            {sortedWeightValues.map((element, index) =>
                                element.value ? (
                                    <tr key={index}>
                                        <td style={{ textAlign: 'left', padding: '5px' }}>
                                            <div style={{
                                                display: 'inline-block',
                                                width: '10px',
                                                height: '10px',
                                                backgroundColor: element.color,
                                                borderRadius: '50%',
                                                marginRight: '5px',
                                                fontSize: 12
                                            }}></div>
                                            {getSymbolHtml(element.sex)}{element.type}:
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px', color: element.displayColor }}>
                                            {element.value !== null && element.value !== undefined
                                                ? `${(element.value >= 10 ? element.value.toFixed(1) : element.value.toFixed(2)).replace('.', ',')} kg`
                                                : '-'}
                                        </td>
                                    </tr>
                                ) : null
                            )}
                        </table>
                    </div>
                    <div style={{
                        width: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: '#fff',
                        borderRadius: '5px',
                        padding: '10px 5px',
                        fontSize: '12px',
                        fontFamily: 'Aniron',
                        margin: isMobileDevice() ? '0 10px' : 0
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px', textAlign: 'center' }}>
                            Gewichtszunahme in %<br />{currentDate} (Tag {currentDateIndex + 1})
                        </div>
                        <table style={{ width: '100%' }}>
                            {sortedPercentValues.map((element, index) =>
                                element.difference ? (
                                    <tr key={index}>
                                        <td style={{ textAlign: 'left', padding: '5px' }}>
                                            <div style={{
                                                display: 'inline-block',
                                                width: '10px',
                                                height: '10px',
                                                backgroundColor: element.color,
                                                borderRadius: '50%',
                                                marginRight: '5px',
                                                fontSize: 12
                                            }}></div>
                                            {getSymbolHtml(element.sex)}{element.type}:
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px' }}>
                                            {element.percentIncrease !== null && element.percentIncrease !== undefined ? `${element.percentIncrease > 0 ? '+' : ''}${element.percentIncrease} %` : '-'}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '5px' }}>
                                            {element.difference !== null && element.difference !== undefined
                                                ? element.difference > 1
                                                    ? `+${(element.difference).toFixed(2).replace('.', ',')} kg`
                                                    : `+${(element.difference * 1000).toFixed(0).replace('.', ',')} g`
                                                : '-'}
                                        </td>
                                    </tr>
                                ) : null
                            )}
                        </table>
                    </div>
                </div>
            ) : null}
            <div style={{ margin: isMobileDevice() ? 10 : 0 }}>
                <BarChartRace data={chartDatasets} startDate={startDate} duration={duration} kombiniert={kombiniert} />
            </div>
            {milestones ? <VerticalTimeline milestones={milestones} /> : null}
        </div>
    );
}

export default ChartWithStats;
